import React, { useRef, useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import BiotechIcon from "@mui/icons-material/Biotech";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PetsIcon from "@mui/icons-material/Pets";
import BedIcon from "@mui/icons-material/Bed";
import { Section, SectionLabel, DP, GradientText } from "./HomeStyles";
import { useReveal } from "../../hooks/useReveal";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

const iconPop = keyframes`
  0%   { transform: scale(1) rotate(0deg); }
  40%  { transform: scale(1.25) rotate(-8deg); }
  70%  { transform: scale(0.95) rotate(4deg); }
  100% { transform: scale(1.1) rotate(0deg); }
`;

const serviceColors = [
  { color: "#E53935", bg: "#FFF5F5" },
  { color: DP.blue,   bg: DP.blueBg },
  { color: "#7B1FA2", bg: "#F5F0FF" },
  { color: "#00897B", bg: "#F0FAF8" },
  { color: "#F57C00", bg: "#FFF8F0" },
  { color: "#C2185B", bg: "#FFF0F8" },
  { color: "#388E3C", bg: "#F0FAF2" },
  { color: "#1565C0", bg: "#EBF3FD" },
];

const serviceIcons = [
  <LocalHospitalIcon />,
  <MedicalServicesIcon />,
  <BiotechIcon />,
  <ScienceIcon />,
  <MonitorHeartIcon />,
  <ContentCutIcon />,
  <PetsIcon />,
  <BedIcon />,
];

const ServiceCardItem: React.FC<{ index: number; visible: boolean }> = ({ index, visible }) => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const { t, isRTL } = useLanguage();
  const item = t.home.services.items[index];
  const svc = serviceColors[index];

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientY - rect.top)  / rect.height - 0.5) * 10;
    const y = ((e.clientX - rect.left) / rect.width  - 0.5) * -10;
    setTilt({ x, y });
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Box ref={cardRef} onMouseMove={handleMouseMove} onMouseEnter={() => setHovered(true)} onMouseLeave={() => { setTilt({ x: 0, y: 0 }); setHovered(false); }}
        sx={{ backgroundColor: DP.white, borderRadius: "20px", p: { xs: 3, md: 3.5 }, border: `1px solid ${hovered ? svc.color + "30" : DP.border}`, height: "100%", cursor: "pointer", position: "relative", overflow: "hidden", transition: "all 0.4s cubic-bezier(0.34,1.56,0.64,1)", transform: visible ? hovered ? `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(-10px) scale(1.02)` : "perspective(800px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)" : "translateY(40px) scale(0.95)", opacity: visible ? 1 : 0, transitionDelay: `${0.05 + index * 0.07}s`, boxShadow: hovered ? `0 28px 64px ${svc.color}22, 0 8px 24px rgba(0,0,0,0.08)` : `0 2px 12px rgba(0,0,0,0.04)`,
          "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: `linear-gradient(90deg, ${svc.color}, ${svc.color}88)`, transform: hovered ? "scaleX(1)" : "scaleX(0)", transformOrigin: isRTL ? "right" : "left", transition: "transform 0.35s ease", borderRadius: "20px 20px 0 0" },
          "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "20px", background: `radial-gradient(circle at ${hovered ? "50% 50%" : "50% 120%"}, ${svc.color}08 0%, transparent 70%)`, transition: "all 0.4s ease" },
        }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "16px", backgroundColor: svc.bg, mb: 2.5, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.3s ease", "& .MuiSvgIcon-root": { color: svc.color, fontSize: "1.6rem" }, ...(hovered ? { animation: `${iconPop} 0.4s ease both` } : {}) }}>
          {serviceIcons[index]}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: DP.textDark, mb: 1, lineHeight: 1.35, textAlign: "center", transition: "color 0.2s ease", ...(hovered ? { color: svc.color } : {}) }}>
          {item.title}
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: DP.textMuted, lineHeight: 1.7 }}>
          {item.desc}
        </Typography>
      </Box>
    </Grid>
  );
};

const ServicesSection: React.FC = () => {
  const { ref, visible } = useReveal(0.1);
  const { t } = useLanguage();
  const ts = t.home.services;

  return (
    <Section id="services" ref={ref} sx={{ backgroundColor: DP.offWhite }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(32px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1)" }}>
          <SectionLabel sx={{ mb: 2 }}>{ts.sectionLabel}</SectionLabel>
          <Typography variant="h2" sx={{ fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 800, color: DP.textDark, letterSpacing: "-0.02em", mb: 2 }}>
            {ts.heading}{" "}<GradientText>{ts.headingHighlight}</GradientText>
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", color: DP.textMuted, maxWidth: 600, mx: "auto", lineHeight: 1.8 }}>
            {ts.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {ts.items.map((_, i) => (
            <ServiceCardItem key={i} index={i} visible={visible} />
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default ServicesSection;
